/**
 * Admin Hotel Concierge — the island operator's dispatch desk for partner
 * hotels.
 *
 * A hotel's concierge phones in and the operator logs the request here. Sending
 * it to drivers puts the job in front of the best few at once and the first to
 * accept takes it (see migration 20260816210000). If nobody answers, the
 * operator is pushed to place it by phone.
 *
 * The distinction that matters on this screen is "waiting on drivers" versus
 * "accepted". Only the second means a driver has committed, and only that is
 * safe to relay back to the hotel. Direct assignment still exists for a job the
 * operator has already agreed on the phone, but it skips the driver's
 * confirmation, so it is deliberately the secondary path.
 *
 * The hotel pays a flat monthly retainer offline, so nothing here touches
 * money. The service record at the bottom is what a retainer review runs on.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import {
  adminApi,
  type ConciergePlace,
  type HotelReportRow,
  type HotelReportTotals,
} from '@/lib/adminApi';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Hotel, Clock, CheckCircle, XCircle, RefreshCw, Plus, Award, WifiOff, Send, Users,
  MapPin, MapPinOff,
} from 'lucide-react';
import { FestivalVenuePinPicker } from '@/components/FestivalVenuePinPicker';
import { UserAvatar } from '@/components/UserAvatar';
import {
  ConciergePinPicker,
  type ConciergePinPatch,
} from '@/components/ConciergePinPicker';
import { ConciergePlaceCombobox } from '@/components/ConciergePlaceCombobox';
import { ContactRow } from '@/components/ContactRow';
import { formatDominicaPhone } from '@/lib/formatPhone';
import { formatDistanceToNow } from 'date-fns';

interface OfferedDriver {
  driver_name: string;
  /** NULL while the driver still has the offer open. */
  response: 'accepted' | 'declined' | 'expired' | 'superseded' | null;
  expires_at: string;
}

interface ConciergeRequest {
  id: string;
  guest_name: string;
  guest_phone?: string | null;
  guest_picture_url?: string | null;
  guest_count?: number;
  hotel_name: string;
  pickup_location: string;
  dropoff_location: string;
  /** Map pins. NULL on requests logged before pinning existed. */
  pickup_lat?: number | null;
  pickup_lng?: number | null;
  dropoff_lat?: number | null;
  dropoff_lng?: number | null;
  pickup_time: string;
  status: string;
  priority?: string;
  notes: string | null;
  driver_id: string | null;
  driver_name?: string | null;
  driver_phone?: string | null;
  driver_license_plate?: string | null;
  created_at: string;
  accepted_at?: string | null;
  /** Offers still ticking in the current wave. */
  live_offers?: number;
  declined_offers?: number;
  offered_drivers?: OfferedDriver[];
  /** Set when a wave produced nothing and a human has to step in. */
  escalated_at?: string | null;
  escalation_reason?: string | null;
}

interface Driver {
  id: string;
  full_name: string;
  license_plate: string;
  phone_number?: string | null;
  profile_picture_url?: string | null;
  is_online?: boolean;
  dda_nise_certified?: boolean;
  location_fresh?: boolean;
}

interface NewRequestForm {
  hotelName: string;
  guestLabel: string;
  guestPhone: string;
  pickupLabel: string;
  dropoffLabel: string;
  priority: 'standard' | 'vip';
  scheduledFor: string;
  notes: string;
  guestCount: string;
  /** Map pins. NULL until the operator drops one. */
  pickupLat: number | null;
  pickupLng: number | null;
  dropoffLat: number | null;
  dropoffLng: number | null;
  /** Add this hotel to the directory once the request is logged. */
  saveHotel: boolean;
}

const EMPTY_FORM: NewRequestForm = {
  hotelName: '',
  guestLabel: '',
  guestPhone: '',
  pickupLabel: '',
  dropoffLabel: '',
  priority: 'standard',
  scheduledFor: '',
  notes: '',
  guestCount: '1',
  pickupLat: null,
  pickupLng: null,
  dropoffLat: null,
  dropoffLng: null,
  saveHotel: false,
};

const statusColors: Record<string, string> = {
  pending: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  offered: 'bg-violet-500/10 text-violet-300 border-violet-500/20',
  assigned: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  completed: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  cancelled: 'bg-gray-700/50 text-gray-400 border-gray-600',
};

/** `assigned` is the wire value; "accepted" is what it now means. */
const statusLabels: Record<string, string> = {
  pending: 'pending',
  offered: 'waiting on drivers',
  assigned: 'accepted',
  completed: 'completed',
  cancelled: 'cancelled',
};

export default function Concierge() {
  const [requests, setRequests] = useState<ConciergeRequest[]>([]);
  const [selected, setSelected] = useState<ConciergeRequest | null>(null);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<NewRequestForm>(EMPTY_FORM);
  /** Request whose pins are being corrected, or null when that dialog is shut. */
  const [repinTarget, setRepinTarget] = useState<ConciergeRequest | null>(null);
  const [places, setPlaces] = useState<ConciergePlace[]>([]);
  const [placesOpen, setPlacesOpen] = useState(false);
  const [report, setReport] = useState<{ hotels: HotelReportRow[]; totals: HotelReportTotals } | null>(null);
  const [reportDays, setReportDays] = useState(30);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const status = statusFilter === 'all' ? undefined : statusFilter;
      const data = await adminApi.getConciergeRequests(status);
      setRequests(Array.isArray(data) ? data : []);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchDrivers = useCallback(async () => {
    try {
      const data = await adminApi.getConciergeDrivers();
      setDrivers(Array.isArray(data) ? data : []);
    } catch { /* silent — the picker degrades to "no drivers online" */ }
  }, []);

  const fetchReport = useCallback(async () => {
    try {
      const data = await adminApi.getConciergeReport(reportDays);
      setReport({ hotels: data.hotels ?? [], totals: data.totals });
    } catch { /* silent — the report is supplementary to dispatch */ }
  }, [reportDays]);

  const fetchPlaces = useCallback(async () => {
    try {
      setPlaces(await adminApi.getConciergePlaces());
    } catch { /* silent — the form still works by typing */ }
  }, []);

  useEffect(() => { fetchRequests(); fetchDrivers(); fetchPlaces(); }, [fetchRequests, fetchDrivers, fetchPlaces]);
  useEffect(() => { fetchReport(); }, [fetchReport]);

  // The RPC returns the whole roster ordered certified-first so the picker can
  // explain itself; only genuinely online drivers are assignable.
  const onlineDrivers = drivers.filter((d) => d.is_online);

  const hotelAlreadySaved = places.some(
    (p) =>
      p.kind === 'hotel' &&
      p.name.trim().toLowerCase() === form.hotelName.trim().toLowerCase(),
  );

  const handleCreate = async () => {
    if (!form.hotelName.trim() || !form.pickupLabel.trim() || !form.dropoffLabel.trim()) {
      toast.error('Hotel, pickup and drop-off are required');
      return;
    }
    setActionLoading(true);
    try {
      await adminApi.createConciergeRequest({
        hotelName: form.hotelName.trim(),
        pickupLabel: form.pickupLabel.trim(),
        dropoffLabel: form.dropoffLabel.trim(),
        guestLabel: form.guestLabel.trim() || null,
        guestPhone: form.guestPhone.trim() || null,
        priority: form.priority,
        // datetime-local yields wall-clock with no zone; the browser parses it
        // in the operator's own timezone, which is the one they typed it in.
        scheduledFor: form.scheduledFor ? new Date(form.scheduledFor).toISOString() : null,
        notes: form.notes.trim() || null,
        guestCount: Math.max(1, Math.min(60, parseInt(form.guestCount, 10) || 1)),
        pickupLat: form.pickupLat,
        pickupLng: form.pickupLng,
        dropoffLat: form.dropoffLat,
        dropoffLng: form.dropoffLng,
      });

      // Saving the hotel is secondary to logging the job, so a failure here
      // must not read as though the request itself failed.
      if (form.saveHotel) {
        try {
          await adminApi.upsertConciergePlace({
            kind: 'hotel',
            name: form.hotelName.trim(),
            spotLabel: form.pickupLabel.trim() || null,
            lat: form.pickupLat,
            lng: form.pickupLng,
            // Deliberately not carried over: the phone on this form belongs to
            // the guest, not the property. Storing it as the hotel's number
            // would leak one guest's contact into every future request.
            defaultPriority: form.priority,
          });
          await fetchPlaces();
        } catch (e: any) {
          toast.warning(`Request logged, but the hotel was not saved: ${e.message}`);
        }
      }

      toast.success('Request logged');
      setForm(EMPTY_FORM);
      setCreateOpen(false);
      fetchRequests();
      fetchReport();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Realtime subscription for live updates
  const unsubRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    unsubRef.current = adminApi.subscribeToConciergeRequests(() => {
      fetchRequests();
    });
    return () => { unsubRef.current?.(); };
  }, [fetchRequests]);

  // The detail panel holds a snapshot, so an offer landing or expiring in the
  // background would otherwise leave the operator reading a stale request.
  useEffect(() => {
    if (!selected) return;
    const fresh = requests.find((r) => r.id === selected.id);
    if (fresh && fresh !== selected) setSelected(fresh);
  }, [requests, selected]);

  const handleDispatch = async (requestId: string) => {
    setActionLoading(true);
    try {
      const { offers, escalated } = await adminApi.dispatchConciergeRequest(requestId);
      if (escalated || offers === 0) {
        toast.error('No driver is available right now. Call someone directly.');
      } else {
        toast.success(`Sent to ${offers} driver${offers === 1 ? '' : 's'}`);
      }
      fetchRequests();
      fetchReport();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssign = async (requestId: string, driverId: string) => {
    setActionLoading(true);
    try {
      await adminApi.assignConciergeRequest(requestId, driverId);
      toast.success('Handed to driver. They were notified but have not confirmed.');
      fetchRequests();
      fetchReport();
      setSelected(null);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async (requestId: string) => {
    setActionLoading(true);
    try {
      await adminApi.completeConciergeRequest(requestId);
      toast.success('Marked as completed');
      fetchRequests();
      fetchReport();
      setSelected(null);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async (requestId: string) => {
    if (!confirm('Cancel this request?')) return;
    setActionLoading(true);
    try {
      await adminApi.cancelConciergeRequest(requestId);
      toast.success('Request cancelled');
      fetchRequests();
      fetchReport();
      setSelected(null);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <RepinDialog
        request={repinTarget}
        onClose={() => setRepinTarget(null)}
        onSaved={() => {
          setRepinTarget(null);
          fetchRequests();
        }}
      />

      <SavedPlacesDialog
        open={placesOpen}
        places={places}
        onClose={() => setPlacesOpen(false)}
        onChanged={fetchPlaces}
      />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        {/* This form is taller than a laptop viewport once the map is in it, so
            the body scrolls on its own and the header and buttons stay put.
            Capping the whole dialog instead would scroll "Log request" out of
            reach, which is the one control that must always be clickable. */}
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-lg max-h-[90vh] flex flex-col gap-0">
          <DialogHeader className="shrink-0">
            <DialogTitle>New concierge request</DialogTitle>
            <DialogDescription className="text-gray-400">
              Log what the hotel just asked for. The guest is not an app user, so a
              name is only so the driver knows who to look for.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 flex-1 overflow-y-auto py-4 pr-1 -mr-1">
            <div>
              <Label className="text-gray-400 text-xs">Hotel *</Label>
              {/* Picking a saved hotel fills the name, the exact spot and the
                  pin in one go — the whole reason the directory exists. */}
              <div className="mt-1 space-y-1.5">
                <ConciergePlaceCombobox
                  places={places}
                  kind="hotel"
                  placeholder="Use a saved hotel…"
                  onPick={(p) =>
                    setForm((f) => ({
                      ...f,
                      hotelName: p.name,
                      pickupLabel: p.spot_label || f.pickupLabel,
                      pickupLat: p.lat,
                      pickupLng: p.lng,
                      priority: p.default_priority,
                      saveHotel: false,
                    }))
                  }
                />
                <Input
                  value={form.hotelName}
                  onChange={(e) => setForm({ ...form, hotelName: e.target.value })}
                  placeholder="Fort Young Hotel"
                  className="bg-gray-950 border-gray-800 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-400 text-xs">Pickup *</Label>
                <Input
                  value={form.pickupLabel}
                  onChange={(e) => setForm({ ...form, pickupLabel: e.target.value })}
                  placeholder="Hotel lobby"
                  className="bg-gray-950 border-gray-800 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Drop-off *</Label>
                <div className="mt-1 space-y-1.5">
                  <ConciergePlaceCombobox
                    places={places}
                    kind="destination"
                    placeholder="Saved destination…"
                    onPick={(p) =>
                      setForm((f) => ({
                        ...f,
                        dropoffLabel: p.spot_label ? `${p.name} · ${p.spot_label}` : p.name,
                        dropoffLat: p.lat,
                        dropoffLng: p.lng,
                      }))
                    }
                  />
                  <Input
                    value={form.dropoffLabel}
                    onChange={(e) => setForm({ ...form, dropoffLabel: e.target.value })}
                    placeholder="Douglas–Charles Airport"
                    className="bg-gray-950 border-gray-800 text-white"
                  />
                </div>
              </div>
            </div>

            <ConciergePinPicker
              pickupLabel={form.pickupLabel}
              dropoffLabel={form.dropoffLabel}
              pickupLat={form.pickupLat}
              pickupLng={form.pickupLng}
              dropoffLat={form.dropoffLat}
              dropoffLng={form.dropoffLng}
              onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
            />

            {/* Only offered for a hotel that isn't in the directory yet —
                otherwise this is a checkbox that does nothing. */}
            {form.hotelName.trim() && !hotelAlreadySaved ? (
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                  checked={form.saveHotel}
                  onCheckedChange={(v) => setForm({ ...form, saveHotel: v === true })}
                  className="mt-0.5"
                />
                <span className="text-xs text-gray-400 leading-relaxed">
                  Save <span className="text-gray-200">{form.hotelName.trim()}</span> for
                  next time. Its pickup spot and pin come back automatically on
                  the next call.
                </span>
              </label>
            ) : null}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-400 text-xs">Guest name</Label>
                <Input
                  value={form.guestLabel}
                  onChange={(e) => setForm({ ...form, guestLabel: e.target.value })}
                  placeholder="Mr Alleyne"
                  className="bg-gray-950 border-gray-800 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Guest phone</Label>
                <Input
                  value={form.guestPhone}
                  onChange={(e) => setForm({ ...form, guestPhone: e.target.value })}
                  placeholder="767 555 0100"
                  className="bg-gray-950 border-gray-800 text-white mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-gray-400 text-xs">How many guests</Label>
              <Input
                type="number"
                min={1}
                max={60}
                value={form.guestCount}
                onChange={(e) => setForm({ ...form, guestCount: e.target.value })}
                className="bg-gray-950 border-gray-800 text-white mt-1 w-28"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Only buses with enough seats will be offered the job
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-400 text-xs">Priority</Label>
                <Select
                  value={form.priority}
                  onValueChange={(v) => setForm({ ...form, priority: v as 'standard' | 'vip' })}
                >
                  <SelectTrigger className="bg-gray-950 border-gray-800 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-800">
                    <SelectItem value="standard" className="text-gray-300 focus:bg-gray-800 focus:text-white">Standard</SelectItem>
                    <SelectItem value="vip" className="text-gray-300 focus:bg-gray-800 focus:text-white">VIP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Pickup time</Label>
                <Input
                  type="datetime-local"
                  value={form.scheduledFor}
                  onChange={(e) => setForm({ ...form, scheduledFor: e.target.value })}
                  className="bg-gray-950 border-gray-800 text-white mt-1"
                />
                <p className="text-[11px] text-gray-500 mt-1">Leave blank for as soon as possible</p>
              </div>
            </div>

            <div>
              <Label className="text-gray-400 text-xs">Notes for the driver</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Two large suitcases, flight at 14:20"
                className="bg-gray-950 border-gray-800 text-white mt-1"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="shrink-0 border-t border-gray-800 pt-4">
            <Button
              variant="ghost"
              className="text-gray-400 hover:text-white"
              onClick={() => setCreateOpen(false)}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
              onClick={handleCreate}
              disabled={actionLoading}
            >
              {actionLoading ? 'Saving…' : 'Log request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Hotel Concierge</h1>
          <p className="text-sm text-gray-400 mt-1">
            Log a pickup when a partner hotel calls, then assign a driver
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="w-4 h-4 mr-1.5" /> New request
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-gray-800 text-gray-300 hover:text-white"
            onClick={() => setPlacesOpen(true)}
          >
            <Hotel className="w-4 h-4 mr-1.5" /> Saved places
          </Button>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 bg-gray-900 border-gray-800 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-800">
              <SelectItem value="all" className="text-gray-300 focus:bg-gray-800 focus:text-white">All Status</SelectItem>
              <SelectItem value="pending" className="text-gray-300 focus:bg-gray-800 focus:text-white">Pending</SelectItem>
              <SelectItem value="offered" className="text-gray-300 focus:bg-gray-800 focus:text-white">Waiting on drivers</SelectItem>
              <SelectItem value="assigned" className="text-gray-300 focus:bg-gray-800 focus:text-white">Accepted</SelectItem>
              <SelectItem value="completed" className="text-gray-300 focus:bg-gray-800 focus:text-white">Completed</SelectItem>
              <SelectItem value="cancelled" className="text-gray-300 focus:bg-gray-800 focus:text-white">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white" onClick={fetchRequests}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Request List */}
        <div className="lg:col-span-2 space-y-2">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-20 bg-gray-800/50 rounded-xl" />
            ))
          ) : requests.length === 0 ? (
            <Card className="bg-gray-900/50 border-gray-800">
              <CardContent className="p-12 text-center">
                <Hotel className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500">No concierge requests</p>
              </CardContent>
            </Card>
          ) : (
            requests.map((req) => (
              <Card
                key={req.id}
                className={`bg-gray-900/50 border-gray-800 cursor-pointer transition-all hover:border-gray-700 ${
                  selected?.id === req.id ? 'border-emerald-500/50 ring-1 ring-emerald-500/20' : ''
                }`}
                onClick={() => setSelected(req)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <UserAvatar size="sm" name={req.guest_name} src={req.guest_picture_url} />
                        <span className="text-white font-medium">{req.guest_name}</span>
                        <Badge className={statusColors[req.status] || statusColors.pending}>
                          {statusLabels[req.status] || req.status.replace(/_/g, ' ')}
                        </Badge>
                        {req.escalated_at && req.status === 'pending' && (
                          <Badge className="bg-red-500/10 text-red-400 border-red-500/20">
                            needs a call
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-400">
                        <span className="text-gray-500">{req.hotel_name}</span>
                        {' → '}
                        {req.dropoff_location}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-xs text-gray-500">
                        {formatDistanceToNow(new Date(req.created_at), { addSuffix: true })}
                      </span>
                      {req.status === 'offered' && (req.live_offers ?? 0) > 0 && (
                        <p className="text-[11px] text-violet-300 mt-0.5">
                          {req.live_offers} deciding
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Detail Panel */}
        <div>
          {selected ? (
            <Card className="bg-gray-900/50 border-gray-800 sticky top-20">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-base">Request Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <UserAvatar size="md" name={selected.guest_name} src={selected.guest_picture_url} />
                    <div>
                      <span className="text-gray-400 text-xs">Guest</span>
                      <p className="text-white">{selected.guest_name}</p>
                    </div>
                  </div>
                  <ContactRow label="Guest phone" value={formatDominicaPhone(selected.guest_phone)} callable />
                  <div className="flex items-center gap-2 text-sm">
                    <Hotel className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-400">Hotel:</span>
                    <span className="text-white">{selected.hotel_name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-400">Pickup:</span>
                    <span className="text-white">
                      {selected.pickup_time ? new Date(selected.pickup_time).toLocaleString() : 'ASAP'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-400">Party:</span>
                    <span className="text-white">
                      {selected.guest_count ?? 1}{' '}
                      {(selected.guest_count ?? 1) === 1 ? 'guest' : 'guests'}
                    </span>
                  </div>
                </div>

                <Separator className="bg-gray-800" />

                <div className="space-y-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Route</p>
                  <RouteEnd
                    label={selected.pickup_location}
                    lat={selected.pickup_lat}
                    lng={selected.pickup_lng}
                  />
                  <p className="text-xs text-gray-500">↓</p>
                  <RouteEnd
                    label={selected.dropoff_location}
                    lat={selected.dropoff_lat}
                    lng={selected.dropoff_lng}
                  />
                  {selected.pickup_lat == null &&
                  selected.status !== 'completed' &&
                  selected.status !== 'cancelled' ? (
                    <button
                      type="button"
                      onClick={() => setRepinTarget(selected)}
                      className="text-xs text-amber-400 hover:text-amber-300 underline decoration-dotted"
                    >
                      No pickup pin. Add one so the driver can navigate.
                    </button>
                  ) : selected.status !== 'completed' && selected.status !== 'cancelled' ? (
                    <button
                      type="button"
                      onClick={() => setRepinTarget(selected)}
                      className="text-xs text-gray-500 hover:text-gray-300 underline decoration-dotted"
                    >
                      Move pins
                    </button>
                  ) : null}
                </div>

                {selected.notes && (
                  <>
                    <Separator className="bg-gray-800" />
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Notes</p>
                      <p className="text-sm text-gray-300">{selected.notes}</p>
                    </div>
                  </>
                )}

                <Separator className="bg-gray-800" />

                {/* Actions */}
                {(selected.status === 'pending' || selected.status === 'offered') && (
                  <div className="space-y-3">
                    {selected.escalated_at && (
                      <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3">
                        <p className="text-sm text-red-300 font-medium">
                          {selected.escalation_reason === 'no_eligible_drivers'
                            ? 'No driver was available'
                            : 'Nobody accepted'}
                        </p>
                        <p className="text-xs text-red-300/80 mt-1">
                          Call a driver directly, or try sending again if someone has
                          since come online.
                        </p>
                      </div>
                    )}

                    {selected.status === 'offered' && (
                      <div className="rounded-lg border border-violet-500/30 bg-violet-500/10 p-3 space-y-1.5">
                        <p className="text-sm text-violet-200 font-medium">
                          Waiting on {selected.live_offers ?? 0}{' '}
                          {(selected.live_offers ?? 0) === 1 ? 'driver' : 'drivers'}
                        </p>
                        {(selected.offered_drivers ?? []).map((o, i) => (
                          <p key={i} className="text-xs text-violet-200/80">
                            {o.driver_name}:{' '}
                            {o.response === null
                              ? 'deciding'
                              : o.response === 'declined'
                                ? 'declined'
                                : o.response === 'expired'
                                  ? 'no answer'
                                  : o.response}
                          </p>
                        ))}
                        <p className="text-[11px] text-violet-200/60 pt-1">
                          Don&apos;t tell the hotel it&apos;s handled until a driver
                          accepts.
                        </p>
                      </div>
                    )}

                    <Button
                      size="sm"
                      className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
                      onClick={() => handleDispatch(selected.id)}
                      disabled={actionLoading}
                    >
                      <Send className="w-3.5 h-3.5 mr-2" />
                      {selected.status === 'offered' ? 'Send to more drivers' : 'Send to drivers'}
                    </Button>
                    <p className="text-[11px] text-gray-500 leading-snug">
                      Offers the job to the best few available drivers at once. First to
                      accept takes it; if nobody answers you&apos;ll be alerted to call
                      someone.
                    </p>

                    <Separator className="bg-gray-800" />

                    <p className="text-xs text-gray-500 uppercase tracking-wider">
                      Or hand it to one driver
                    </p>
                    <p className="text-[11px] text-gray-500 leading-snug -mt-1">
                      Skips the accept step. Only use this once you&apos;ve spoken to
                      them.
                    </p>
                    {onlineDrivers.length === 0 ? (
                      <p className="text-sm text-gray-500">No drivers online</p>
                    ) : (
                      <div className="space-y-2">
                        {onlineDrivers.slice(0, 6).map((d) => (
                          <Button
                            key={d.id}
                            variant="outline"
                            size="sm"
                            className="w-full justify-start border-gray-700 text-gray-300 hover:text-white hover:border-emerald-500/50 h-auto py-2"
                            onClick={() => handleAssign(selected.id, d.id)}
                            disabled={actionLoading}
                          >
                            <UserAvatar size="sm" name={d.full_name} src={d.profile_picture_url} className="mr-2" />
                            <span className="truncate">{d.full_name}</span>
                            {d.dda_nise_certified && (
                              <span title="DDA/NISE tourism certified" className="ml-1.5 shrink-0">
                                <Award className="w-3.5 h-3.5 text-amber-400" />
                              </span>
                            )}
                            {d.location_fresh === false && (
                              <span
                                title="No GPS in the last 10 minutes. Confirm by phone before assigning."
                                className="ml-1.5 shrink-0"
                              >
                                <WifiOff className="w-3.5 h-3.5 text-red-400" />
                              </span>
                            )}
                            <span className="font-mono text-xs text-gray-500 ml-auto">{d.license_plate}</span>
                          </Button>
                        ))}
                        <p className="text-[11px] text-gray-500 leading-snug">
                          <Award className="w-3 h-3 inline text-amber-400" /> tourism certified ·{' '}
                          <WifiOff className="w-3 h-3 inline text-red-400" /> GPS stale. Certified
                          drivers are listed first.
                        </p>
                      </div>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => handleCancel(selected.id)}
                      disabled={actionLoading}
                    >
                      <XCircle className="w-3.5 h-3.5 mr-2" /> Cancel Request
                    </Button>
                  </div>
                )}

                {selected.driver_name && (
                  <div className="space-y-1">
                    <p className="text-xs text-gray-500 uppercase tracking-wider">
                      {selected.accepted_at ? 'Accepted by' : 'Handed to (unconfirmed)'}
                    </p>
                    <p className="text-sm text-white">{selected.driver_name}</p>
                    <ContactRow label="Phone" value={formatDominicaPhone(selected.driver_phone)} callable />
                    <ContactRow label="Plate" value={selected.driver_license_plate} />
                  </div>
                )}

                {selected.status === 'assigned' && (
                  <div className="space-y-2">
                    <Button
                      size="sm"
                      className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
                      onClick={() => handleComplete(selected.id)}
                      disabled={actionLoading}
                    >
                      <CheckCircle className="w-3.5 h-3.5 mr-2" /> Mark Complete
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => handleCancel(selected.id)}
                      disabled={actionLoading}
                    >
                      <XCircle className="w-3.5 h-3.5 mr-2" /> Cancel
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-gray-900/50 border-gray-800">
              <CardContent className="p-8 text-center">
                <Hotel className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Select a request to view details</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Service record — the evidence behind a retainer review. */}
      <Card className="bg-gray-900/50 border-gray-800">
        <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
          <div>
            <CardTitle className="text-white text-base">Service record</CardTitle>
            <p className="text-xs text-gray-500 mt-1">
              What each property got, and how fast we answered
            </p>
          </div>
          <Select value={String(reportDays)} onValueChange={(v) => setReportDays(Number(v))}>
            <SelectTrigger className="w-28 bg-gray-900 border-gray-800 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-800">
              <SelectItem value="7" className="text-gray-300 focus:bg-gray-800 focus:text-white">7 days</SelectItem>
              <SelectItem value="30" className="text-gray-300 focus:bg-gray-800 focus:text-white">30 days</SelectItem>
              <SelectItem value="90" className="text-gray-300 focus:bg-gray-800 focus:text-white">90 days</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="p-0">
          {!report || report.hotels.length === 0 ? (
            <div className="p-10 text-center">
              <Hotel className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500">
                No requests in this window yet
              </p>
            </div>
          ) : (
            <>
              <div className="px-6 pb-3 text-sm text-gray-400">
                {report.totals.requests} request{report.totals.requests === 1 ? '' : 's'} across{' '}
                {report.totals.hotels} propert{report.totals.hotels === 1 ? 'y' : 'ies'} ·{' '}
                {report.totals.completed} completed
                {report.totals.median_minutes_to_assign != null && (
                  <> · median {report.totals.median_minutes_to_assign} min to assign a driver</>
                )}
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-800 hover:bg-transparent">
                    <TableHead className="text-gray-400">Hotel</TableHead>
                    <TableHead className="text-gray-400">Requests</TableHead>
                    <TableHead className="text-gray-400">Completed</TableHead>
                    <TableHead className="text-gray-400">Cancelled</TableHead>
                    <TableHead className="text-gray-400">Median to assign</TableHead>
                    <TableHead className="text-gray-400">Slowest (p90)</TableHead>
                    <TableHead className="text-gray-400">Last call</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.hotels.map((h) => (
                    <TableRow key={h.hotel_name} className="border-gray-800 hover:bg-gray-800/30">
                      <TableCell className="text-white">
                        {h.hotel_name}
                        {h.vip > 0 && (
                          <Badge className="ml-2 bg-amber-500/10 text-amber-400 border-amber-500/20">
                            {h.vip} VIP
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-300">
                        {h.requests}
                        {h.open > 0 && (
                          <span className="text-xs text-amber-400 ml-1.5">{h.open} open</span>
                        )}
                      </TableCell>
                      <TableCell className="text-emerald-400">{h.completed}</TableCell>
                      <TableCell className={h.cancelled > 0 ? 'text-red-400' : 'text-gray-500'}>
                        {h.cancelled}
                      </TableCell>
                      <TableCell className="text-gray-300">
                        {h.median_minutes_to_assign != null ? `${h.median_minutes_to_assign} min` : '–'}
                      </TableCell>
                      <TableCell className="text-gray-400">
                        {h.p90_minutes_to_assign != null ? `${h.p90_minutes_to_assign} min` : '–'}
                      </TableCell>
                      <TableCell className="text-gray-400 text-sm">
                        {h.last_request_at
                          ? formatDistanceToNow(new Date(h.last_request_at), { addSuffix: true })
                          : '–'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * The saved hotels and destinations directory.
 *
 * Kept as a dialog on the dispatch screen rather than its own page: the only
 * time an operator thinks about this list is while logging a request that
 * revealed a property is missing or its pin is wrong, and making them navigate
 * away mid-call would lose the request they were typing.
 */
function SavedPlacesDialog({
  open,
  places,
  onClose,
  onChanged,
}: {
  open: boolean;
  places: ConciergePlace[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState<ConciergePlace | null>(null);
  const [draft, setDraft] = useState<{
    kind: 'hotel' | 'destination';
    name: string;
    spotLabel: string;
    lat: number | null;
    lng: number | null;
    contactPhone: string;
    defaultPriority: 'standard' | 'vip';
  } | null>(null);
  const [saving, setSaving] = useState(false);

  const startNew = (kind: 'hotel' | 'destination') => {
    setEditing(null);
    setDraft({
      kind,
      name: '',
      spotLabel: '',
      lat: null,
      lng: null,
      contactPhone: '',
      defaultPriority: 'standard',
    });
  };

  const startEdit = (p: ConciergePlace) => {
    setEditing(p);
    setDraft({
      kind: p.kind,
      name: p.name,
      spotLabel: p.spot_label ?? '',
      lat: p.lat,
      lng: p.lng,
      contactPhone: p.contact_phone ?? '',
      defaultPriority: p.default_priority,
    });
  };

  const save = async () => {
    if (!draft?.name.trim()) {
      toast.error('A name is required');
      return;
    }
    setSaving(true);
    try {
      await adminApi.upsertConciergePlace({
        id: editing?.id ?? null,
        kind: draft.kind,
        name: draft.name.trim(),
        spotLabel: draft.spotLabel.trim() || null,
        lat: draft.lat,
        lng: draft.lng,
        contactPhone: draft.contactPhone.trim() || null,
        defaultPriority: draft.defaultPriority,
      });
      toast.success('Saved');
      setDraft(null);
      setEditing(null);
      onChanged();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const archive = async (p: ConciergePlace) => {
    try {
      await adminApi.setConciergePlaceActive(p.id, false);
      toast.success(`${p.name} archived`);
      onChanged();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const hotels = places.filter((p) => p.kind === 'hotel');
  const destinations = places.filter((p) => p.kind === 'destination');

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          setDraft(null);
          setEditing(null);
          onClose();
        }
      }}
    >
      <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Saved places</DialogTitle>
          <DialogDescription className="text-gray-400">
            Pin a property once and it comes back on every future request for it.
            Archiving hides a place from the pickers without touching the requests
            that already used it.
          </DialogDescription>
        </DialogHeader>

        {draft ? (
          <div className="space-y-3">
            <div>
              <Label className="text-gray-400 text-xs">
                {draft.kind === 'hotel' ? 'Hotel name' : 'Destination name'} *
              </Label>
              <Input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder={
                  draft.kind === 'hotel' ? 'Fort Young Hotel' : 'Douglas–Charles Airport'
                }
                className="bg-gray-950 border-gray-800 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-gray-400 text-xs">Exact spot</Label>
              <Input
                value={draft.spotLabel}
                onChange={(e) => setDraft({ ...draft, spotLabel: e.target.value })}
                placeholder="Main lobby, Victoria Street entrance"
                className="bg-gray-950 border-gray-800 text-white mt-1"
              />
              <p className="text-[11px] text-gray-600 mt-1">
                Where the driver actually waits. The pin is a door, not a building.
              </p>
            </div>

            {draft.kind === 'hotel' ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-400 text-xs">Hotel phone</Label>
                  <Input
                    value={draft.contactPhone}
                    onChange={(e) => setDraft({ ...draft, contactPhone: e.target.value })}
                    placeholder="767 555 0100"
                    className="bg-gray-950 border-gray-800 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">Standing priority</Label>
                  <Select
                    value={draft.defaultPriority}
                    onValueChange={(v) =>
                      setDraft({ ...draft, defaultPriority: v as 'standard' | 'vip' })
                    }
                  >
                    <SelectTrigger className="bg-gray-950 border-gray-800 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-gray-800">
                      <SelectItem value="standard" className="text-gray-300">Standard</SelectItem>
                      <SelectItem value="vip" className="text-gray-300">VIP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : null}

            <div className="rounded-lg border border-gray-800 bg-gray-950/60 p-3">
              <Label className="text-gray-300 text-xs flex items-center gap-2 mb-2">
                <MapPin className="w-3.5 h-3.5" /> Pin
              </Label>
              <FestivalVenuePinPicker
                lat={draft.lat}
                lng={draft.lng}
                themeColor={draft.kind === 'hotel' ? '#10b981' : '#f59e0b'}
                emoji={draft.kind === 'hotel' ? '🏨' : '🏁'}
                label={draft.name || draft.spotLabel || 'Pin'}
                haloRadiusM={null}
                onChange={(lat, lng) => setDraft({ ...draft, lat, lng })}
              />
            </div>

            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => {
                  setDraft(null);
                  setEditing(null);
                }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button onClick={save} disabled={saving}>
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Add place'}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <PlaceGroup
              title="Hotels"
              places={hotels}
              emptyHint="No hotels saved yet. Add one and its pin returns on every future call."
              onEdit={startEdit}
              onArchive={archive}
              onAdd={() => startNew('hotel')}
            />
            <Separator className="bg-gray-800" />
            <PlaceGroup
              title="Destinations"
              places={destinations}
              emptyHint="No destinations saved."
              onEdit={startEdit}
              onArchive={archive}
              onAdd={() => startNew('destination')}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PlaceGroup({
  title,
  places,
  emptyHint,
  onEdit,
  onArchive,
  onAdd,
}: {
  title: string;
  places: ConciergePlace[];
  emptyHint: string;
  onEdit: (p: ConciergePlace) => void;
  onArchive: (p: ConciergePlace) => void;
  onAdd: () => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-gray-500 uppercase tracking-wider">{title}</p>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onAdd}>
          <Plus className="w-3 h-3 mr-1" /> Add
        </Button>
      </div>

      {places.length === 0 ? (
        <p className="text-xs text-gray-600">{emptyHint}</p>
      ) : (
        <div className="space-y-1.5">
          {places.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-2 rounded-md border border-gray-800 bg-gray-950/60 px-2.5 py-2"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-gray-200 truncate">{p.name}</span>
                  {p.default_priority === 'vip' ? (
                    <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[10px] px-1.5 py-0">
                      VIP
                    </Badge>
                  ) : null}
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                  {p.lat != null ? (
                    <MapPin className="w-3 h-3 text-emerald-500 shrink-0" />
                  ) : (
                    <MapPinOff className="w-3 h-3 text-amber-500 shrink-0" />
                  )}
                  <span className="truncate">
                    {p.spot_label || (p.lat != null ? 'pinned' : 'no pin yet')}
                  </span>
                  {p.use_count > 0 ? (
                    <span className="text-gray-600 shrink-0">· {p.use_count} used</span>
                  ) : null}
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs shrink-0"
                onClick={() => onEdit(p)}
              >
                Edit
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs text-gray-500 hover:text-red-400 shrink-0"
                onClick={() => onArchive(p)}
              >
                Archive
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * One end of the route, with its pin state made obvious. An operator relaying
 * "the driver is on the way" to a hotel needs to know at a glance whether that
 * driver actually has a coordinate to drive to.
 */
function RouteEnd({
  label,
  lat,
  lng,
}: {
  label: string;
  lat?: number | null;
  lng?: number | null;
}) {
  const pinned = lat != null && lng != null;
  return (
    <div>
      <p className="text-sm text-gray-300">{label}</p>
      {pinned ? (
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
          target="_blank"
          rel="noreferrer"
          className="text-[11px] text-emerald-400/80 hover:text-emerald-300"
        >
          pinned · {lat!.toFixed(5)}, {lng!.toFixed(5)}
        </a>
      ) : (
        <p className="text-[11px] text-gray-600">no pin, text search only</p>
      )}
    </div>
  );
}

/**
 * Corrects the pins on a request that is already open. The driver is normally
 * the one who finds out a pin is wrong, so this has to be fixable without
 * cancelling and rebuilding the job (which would throw away its dispatch
 * history and the hotel's place in the service record).
 */
function RepinDialog({
  request,
  onClose,
  onSaved,
}: {
  request: ConciergeRequest | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [pins, setPins] = useState<ConciergePinPatch>({});
  const [saving, setSaving] = useState(false);

  // Reset to the request's stored pins each time a different request opens,
  // otherwise the previous job's edits bleed into this one.
  useEffect(() => {
    setPins(
      request
        ? {
            pickupLat: request.pickup_lat ?? null,
            pickupLng: request.pickup_lng ?? null,
            dropoffLat: request.dropoff_lat ?? null,
            dropoffLng: request.dropoff_lng ?? null,
          }
        : {},
    );
  }, [request]);

  const save = async () => {
    if (!request) return;
    setSaving(true);
    try {
      await adminApi.setConciergeLocations(request.id, pins);
      toast.success('Pins updated');
      onSaved();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={request !== null} onOpenChange={(open) => !open && onClose()}>
      {/* Same shape as the create dialog: only the map area scrolls, so Save
          pins stays reachable on a short viewport. */}
      <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-lg max-h-[90vh] flex flex-col gap-0">
        <DialogHeader className="shrink-0">
          <DialogTitle>Move pins</DialogTitle>
          <DialogDescription className="text-gray-400">
            {request?.hotel_name}: the driver navigates to whatever is pinned
            here. Changes apply immediately, including to a driver already on
            the way.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 pr-1 -mr-1">
          {request ? (
            <ConciergePinPicker
              pickupLabel={request.pickup_location}
              dropoffLabel={request.dropoff_location}
              pickupLat={pins.pickupLat ?? null}
              pickupLng={pins.pickupLng ?? null}
              dropoffLat={pins.dropoffLat ?? null}
              dropoffLng={pins.dropoffLng ?? null}
              onChange={(patch) => setPins((p) => ({ ...p, ...patch }))}
            />
          ) : null}
        </div>

        <DialogFooter className="shrink-0 border-t border-gray-800 pt-4">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save pins'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
